/**
 * Guía de vida útil por tipo de equipo (tabla de Alex, feedback 3).
 *
 * Son estimaciones orientativas de años de amortización según el tipo de
 * inversión. El formulario de inversiones las usa para SUGERIR los años de
 * vida útil (hoy ponía 5 a ciegas), y Alexia las usa para responder
 * "¿cuántos años se amortiza un horno?".
 *
 * `suggested` = punto medio del rango, redondeado. El usuario siempre puede
 * cambiarlo: son orientativas, dependen de calidad, uso y mantenimiento.
 */

export interface UsefulLifeItem {
  name: string;
  minYears: number;
  maxYears: number;
  /** Años sugeridos (punto medio del rango). */
  suggested: number;
}

export interface UsefulLifeCategory {
  category: string;
  items: UsefulLifeItem[];
}

function item(name: string, minYears: number, maxYears: number): UsefulLifeItem {
  return { name, minYears, maxYears, suggested: Math.round((minYears + maxYears) / 2) };
}

export const USEFUL_LIFE_GUIDE: UsefulLifeCategory[] = [
  {
    category: 'Cocina y preparación',
    items: [
      item('Estufas industriales', 8, 12),
      item('Horno combinado', 8, 10),
      item('Refrigeradores comerciales', 8, 12),
      item('Procesador de alimentos', 5, 7),
      item('Mesas de acero inoxidable', 10, 15),
      item('Licuadoras y batidoras', 5, 7),
      item('Planchas y parrillas', 8, 12),
      item('Ollas, sartenes y utensilios', 3, 5),
      item('Insertos de acero', 5, 7),
    ],
  },
  {
    category: 'Vajilla, cristalería y menaje',
    items: [
      item('Platos y tazones', 3, 5),
      item('Tazas y mugs', 3, 5),
      item('Cristalería (copas y vasos)', 2, 4),
      item('Cubiertos', 3, 5),
      item('Utensilios de servicio (pinzas y cucharones)', 2, 4),
      item('Bandejas para servicio', 5, 8),
      item('Tablas de corte', 2, 3),
    ],
  },
  {
    category: 'Área de servicio',
    items: [
      item('Barra de servicio', 10, 15),
      item('Máquina de café', 8, 10),
      item('Vitrinas refrigeradas', 8, 10),
      item('Sistemas de dispensación de bebidas', 8, 10),
    ],
  },
  {
    category: 'Exterior',
    items: [
      item('Mobiliario de terraza', 5, 8),
      item('Sombrillas o pérgolas', 5, 8),
      item('Jardinería', 3, 5),
      item('Iluminación exterior', 8, 10),
    ],
  },
  {
    category: 'Mobiliario',
    items: [
      item('Mesas y sillas para clientes', 5, 10),
      item('Bancos y sofás', 8, 10),
      item('Estanterías de almacenaje', 10, 15),
    ],
  },
  {
    category: 'Decoración e iluminación',
    items: [
      item('Lámparas y sistemas de iluminación', 8, 10),
      item('Paredes decorativas', 5, 15),
      item('Arte y decoración', 10, 15),
    ],
  },
  {
    category: 'Tecnología',
    items: [
      item('Sistema POS (Punto de Venta)', 5, 8),
      item('Cámaras de seguridad', 5, 7),
      item('Pantallas de menús digitales', 5, 8),
      item('Sistema de sonido', 5, 7),
      item('Relojes y timbradores', 5, 7),
    ],
  },
  {
    category: 'Instalaciones generales',
    items: [
      item('Campanas de extracción', 10, 15),
      item('Aires acondicionados', 10, 12),
      item('Sistemas de ventilación', 10, 15),
      item('Sistema eléctrico', 15, 20),
      item('Sistema de fontanería', 15, 20),
    ],
  },
  {
    category: 'Área de limpieza',
    items: [
      item('Lavavajillas industrial', 8, 10),
      item('Equipos de limpieza', 3, 5),
      item('Fregaderos de acero inoxidable', 10, 15),
    ],
  },
  // Bloque I (reglas de Inversiones, 11 oct 2026): obras y cómputo.
  {
    category: 'Obras y oficina',
    items: [
      // «Lo que dure el contrato de renta»: sin contrato, de 5 a 10 años.
      item('Obras y reformas del local', 5, 10),
      item('Equipo de cómputo', 3, 4),
      item('Tablets', 3, 3),
    ],
  },
];

/** Todos los ítems en plano, con su categoría, para búsquedas y selectores. */
export const USEFUL_LIFE_ITEMS: Array<UsefulLifeItem & { category: string }> =
  USEFUL_LIFE_GUIDE.flatMap((c) => c.items.map((i) => ({ ...i, category: c.category })));

function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Busca el tipo de equipo que mejor casa con un texto libre (p. ej. "horno",
 * "cafetera", "tpv"). Devuelve el ítem con más palabras en común, o undefined.
 * Pensado para el formulario (sugerir años) y para Alexia.
 */
export function suggestUsefulLife(
  query: string,
): (UsefulLifeItem & { category: string }) | undefined {
  const needle = normalize(query);
  if (!needle) return undefined;
  const tokens = needle.split(' ').filter((t) => t.length >= 3);
  if (tokens.length === 0) return undefined;

  let best: (UsefulLifeItem & { category: string }) | undefined;
  let bestScore = 0;
  for (const it of USEFUL_LIFE_ITEMS) {
    const hay = normalize(`${it.name} ${it.category}`);
    let score = 0;
    for (const token of tokens) {
      if (hay.includes(token)) score += token.length;
    }
    // Sinónimos frecuentes que no aparecen literalmente en la tabla.
    if (/\b(tpv|caja registradora|datafono)\b/.test(needle) && /pos/.test(hay)) score += 5;
    if (/\bcafetera\b/.test(needle) && /cafe/.test(hay)) score += 5;
    if (/\bnevera|frigo(rifico)?\b/.test(needle) && /refrigerador/.test(hay)) score += 5;
    // Bloque I: más sinónimos de cocina y sala.
    if (/\b(mixto|combi|rational)\b/.test(needle) && /horno combinado/.test(hay)) score += 8;
    if (/\b(cuberteria|cubiertos|tenedores|cucharas)\b/.test(needle) && /^cubiertos/.test(hay)) score += 8;
    if (/\b(vajilla|platos?)\b/.test(needle) && /^platos/.test(hay)) score += 6;
    if (/\b(vasos|copas)\b/.test(needle) && /cristaleria/.test(hay)) score += 6;
    if (/\b(sillas?|mesas?)\b/.test(needle) && !/acero|inox/.test(needle) && /clientes/.test(hay)) score += 6;
    if (/\b(obra|obras|reforma|remodelacion|instalacion)\b/.test(needle) && /obras y reformas/.test(hay)) score += 8;
    if (/\b(computadora|laptop|ordenador|portatil|impresora)\b/.test(needle) && /computo/.test(hay)) score += 8;
    if (/\b(tablet|ipad)\b/.test(needle) && /tablets/.test(hay)) score += 8;
    if (/\b(estufa|fogon|fogones|cocina industrial)\b/.test(needle) && /estufas/.test(hay)) score += 6;
    if (/\b(extractor|extraccion)\b/.test(needle) && /campanas/.test(hay)) score += 6;
    if (/\b(freidora)\b/.test(needle) && /planchas/.test(hay)) score += 4;
    if (score > bestScore) {
      bestScore = score;
      best = it;
    }
  }
  return bestScore > 0 ? best : undefined;
}
